import z from 'zod';
import { rateTypeSchema } from './payment-summary.schema.ts';

export const invoiceAdjustmentRateTypeSchema = z.enum([
	'Monthly',
	'Validation',
	'Hotel Storage',
	'Office/Retail Rent',
	'Miscellaneous',
]);

export const invoiceAdjustmentFeeTypeSchema = z.enum([
	'Access Device',
	'Activation',
	'Beginning Balance',
	'Credit Card',
	'Late',
	'Recurring Late',
	'Lost Device',
	'Mail Invoice',
	'Monthly',
	'Nonrecurring Payment',
	'Transaction',
	'Validation',
	'Hotel Storage',
	'Office/Retail Rent',
	'Manual Monthly',
	'Miscellaneous',
	'Applied Credit',
	'Carryover',
	'Daily & Hourly',
	'Hourly',
	'Tier',
	'Vehicle Surcharge',
]);

export const invoiceAdjustmentRateTypes = invoiceAdjustmentRateTypeSchema.options;

export const invoiceAdjustmentReportFormatSchema = z.enum(['csv', 'pdf']);

const filterSchema = z.discriminatedUnion('type', [
	z.object({
		type: z.literal('accountId'),
		value: z.number().int(),
	}),
	z.object({
		type: z.literal('subscriptionName'),
		value: z.string(),
	}),
]);

export const invoiceAdjustmentParametersSchema = z
	.object({
		facilityIds: z.array(z.string()).optional(),
		accountFilter: filterSchema.optional(),
		invoiceStartDate: z.iso.date().optional(),
		invoiceEndDate: z.iso.date().optional(),
		parkingPeriodStartMonth: z.number().int().min(0).max(11).optional(),
		parkingPeriodStartYear: z.number().int().min(2021).max(2030).optional(),
		parkingPeriodEndMonth: z.number().int().min(0).max(11).optional(),
		parkingPeriodEndYear: z.number().int().min(2021).max(2030).optional(),
		rateType: z.array(invoiceAdjustmentRateTypeSchema).min(1).optional(),
		feeType: z.array(invoiceAdjustmentFeeTypeSchema).optional(),
		subscriberId: z.string().optional(),
		subscriptionName: z.string().optional(),
		payorType: z.string().optional(),

		// Added report column fields
		invoiceId: z.string().optional(),
		adjustmentDate: z.iso.date().optional(),
		adjustmentType: z.string().optional(),
		accountId: z.number().int().optional(),
		parkerFirstName: z.string().optional(),
		parkerLastName: z.string().optional(),
		netInvoiceAmount: z.number().optional(),
		taxesAmount: z.number().optional(),
		grossInvoiceAmount: z.number().optional(),
		transactionNotes: z.string().optional(),
		facilityName: z.string().optional(),
		facilityNumber: z.union([z.string(), z.number()]).optional(),
		format: invoiceAdjustmentReportFormatSchema.default('csv'),
	})
	.superRefine((value, ctx) => {
		const hasInvoiceStartDate = value.invoiceStartDate !== undefined;
		const hasParkingPeriodStart =
			value.parkingPeriodStartMonth !== undefined && value.parkingPeriodStartYear !== undefined;

		if (!hasInvoiceStartDate && !hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceStartDate'],
				message: 'Either invoiceStartDate or parking period start month and year is required',
			});
		}

		if (value.invoiceEndDate && !hasInvoiceStartDate) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceEndDate'],
				message: 'Cannot specify invoiceEndDate without invoiceStartDate',
			});
		}

		if (
			(value.parkingPeriodEndMonth !== undefined || value.parkingPeriodEndYear !== undefined) &&
			!hasParkingPeriodStart
		) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndMonth'],
				message: 'Cannot specify a parking period end without a parking period start',
			});
		}

		if (value.parkingPeriodEndMonth !== undefined && value.parkingPeriodEndYear === undefined) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndYear'],
				message: 'parkingPeriodEndYear is required when parkingPeriodEndMonth is provided',
			});
		}

		if (value.parkingPeriodEndYear !== undefined && value.parkingPeriodEndMonth === undefined) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndMonth'],
				message: 'parkingPeriodEndMonth is required when parkingPeriodEndYear is provided',
			});
		}

		if (
			value.parkingPeriodStartMonth !== undefined &&
			value.parkingPeriodStartYear !== undefined &&
			value.parkingPeriodEndMonth !== undefined &&
			value.parkingPeriodEndYear !== undefined
		) {
			const startValue = value.parkingPeriodStartYear * 12 + value.parkingPeriodStartMonth;
			const endValue = value.parkingPeriodEndYear * 12 + value.parkingPeriodEndMonth;

			if (endValue < startValue) {
				ctx.addIssue({
					code: 'custom',
					path: ['parkingPeriodEndMonth'],
					message: 'Parking period end must be on or after parking period start',
				});
			}
		}
	});

export type InvoiceAdjustmentParameters = z.infer<typeof invoiceAdjustmentParametersSchema>;
export type InvoiceAdjustmentRateTypes = z.infer<typeof invoiceAdjustmentRateTypeSchema>;
export type InvoiceAdjustmentReportFormat = z.infer<typeof invoiceAdjustmentReportFormatSchema>;
